import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';

import { usePagadas } from '#components/mobile/budget/usePagadas';
import { InputField } from '#components/mobile/MobileForms';
import { Gavi } from '#components/mobile/travesia/Gavi';
import { Boton } from '#components/mobile/ui/Boton';
import { Hoja } from '#components/mobile/ui/Hoja';
import { estiloTarjeta } from '#components/mobile/ui/Tarjeta';
import { color, radio, TACTIL, texto } from '#components/mobile/ui/tokens';
import { registrarUso } from '#uso';

import { sumarDias, sumarMeses } from './cobros';
import type { SerieCobro } from './cobros';
import { fechaRenovacion, tipoDeSerie } from './consejos';
import type { Consejo } from './consejos';
import {
  conBaja,
  conContratoDe,
  conRecordar,
  conRespuesta,
  conSilencio,
  contratoDe,
} from './estadoGavi';
import type { Contrato, EstadoGavi, Respuesta } from './estadoGavi';
import { useFormatosGavi } from './formatos';
import { textoConsejo } from './textosConsejo';
import { TIPOS_RECIBO } from './tiposRecibo';
import { usePagosGavi } from './usePagosGavi';
import { EMOJI_TIPO, useTextoTipo } from './useTextoTipo';

/** En la ficha, como mucho dos consejos a la vez (los demás esperan). */
const MAX_CONSEJOS = 2;

type GaviFichaProps = {
  categoriaId: string;
  nombre: string;
  month: string;
};

/**
 * Gavi en la ficha de un pago fijo (Altamar): sus consejos con los números de la persona, la tarjeta
 * «Contrato» (tipo, renovación, permanencia y fin de la promoción, que Gavi
 * saca del historial y la persona confirma) y «Lo he dado de baja». El cabo
 * de los recibos va en «Evolución» (`RecibosGavi`).
 */
export function GaviFicha({ categoriaId, nombre, month }: GaviFichaProps) {
  const { fijos } = usePagadas(month);
  const gavi = usePagosGavi(fijos);
  if (!gavi.activo || gavi.cargando || !fijos.has(categoriaId)) {
    return null;
  }
  const series = gavi.porCategoria.get(categoriaId) ?? [];
  const consejos = gavi.consejos.get(categoriaId) ?? [];
  if (series.length === 0 && consejos.length === 0) {
    return null;
  }
  const claves = series.map(s => s.clave);
  // Con varios recibos en la categoría (dos seguros), cada uno el suyo.
  const varios = series.length > 1;
  const hoy = monthUtils.currentDay();
  return (
    <View style={{ gap: 12 }} data-testid="gavi-ficha">
      {consejos.slice(0, MAX_CONSEJOS).map(c => (
        <TarjetaConsejo
          key={c.clave}
          consejo={c}
          estado={gavi.estado}
          cambiar={gavi.cambiar}
          hoy={hoy}
        />
      ))}
      {series.map((serie, i) => (
        <View key={serie.clave} style={{ gap: 12 }}>
          <TarjetaContrato
            nombreCategoria={nombre}
            serie={serie}
            conComercio={varios}
            contrato={contratoDe(gavi.estado, categoriaId, claves, i)}
            cambiarContrato={cambios =>
              gavi.cambiar(e =>
                conContratoDe(e, categoriaId, claves, i, cambios),
              )
            }
            inicioCuenta={gavi.inicioCuenta}
            hoy={hoy}
          />
          <DadoDeBaja
            serie={serie}
            conComercio={varios}
            fecha={gavi.estado.bajas[serie.clave] ?? null}
            cambiar={gavi.cambiar}
            hoy={hoy}
          />
        </View>
      ))}
    </View>
  );
}

const estiloBotonPequeno = { minHeight: 40, padding: '0 14px', fontSize: 14 };

/** Cuándo vuelve «Recuérdamelo»: 6 semanas antes de su fecha o en 2 semanas. */
export function fechaRecordar(c: Consejo, hoy: string): string {
  const fecha = c.datos.fecha;
  if (fecha) {
    const antes = sumarDias(fecha, -42);
    if (antes > hoy) {
      return antes;
    }
  }
  return sumarDias(hoy, 14);
}

function TarjetaConsejo({
  consejo,
  estado,
  cambiar,
  hoy,
}: {
  consejo: Consejo;
  estado: EstadoGavi;
  cambiar: (fn: (e: EstadoGavi) => EstadoGavi) => void;
  hoy: string;
}) {
  const { t } = useTranslation();
  const formatos = useFormatosGavi();
  const [guion, setGuion] = useState(false);
  const respuesta = consejo.pregunta
    ? estado.respuestas[consejo.pregunta]
    : undefined;
  const txt = textoConsejo(t, formatos, consejo, respuesta);
  const responder = (r: Respuesta) => {
    registrarUso('gavi_pagos_fijos', { accion: 'respuesta', tipo: consejo.id });
    cambiar(e => conRespuesta(e, consejo.pregunta ?? consejo.clave, r));
  };
  const silenciar = () => {
    registrarUso('gavi_pagos_fijos', { accion: 'llamado', tipo: consejo.id });
    cambiar(e => conSilencio(e, consejo.clave, sumarMeses(hoy, 12)));
  };
  const recordar = fechaRecordar(consejo, hoy);

  const preguntaCoche = consejo.id === 'coche-cobertura' && respuesta == null;
  const preguntaConsumo = consejo.id === 'consumo-subida' && respuesta == null;

  return (
    <View
      style={{ ...estiloTarjeta('normal', 14), gap: 12 }}
      data-testid={`gavi-consejo-${consejo.id}`}
    >
      <View style={{ flexDirection: 'row', gap: 10, alignItems: 'flex-start' }}>
        <Gavi
          estado={consejo.prioridad === 1 ? 'ojos-plato' : 'ladea'}
          ancho={44}
          style={{ flexShrink: 0 }}
        />
        <Text style={{ ...texto.cuerpo, flex: 1, color: color.fg }}>
          {txt.texto}
          {txt.pregunta && !preguntaCoche ? ` ${txt.pregunta}` : ''}
        </Text>
      </View>

      {preguntaConsumo && (
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
          <Boton
            variante="aviso"
            style={estiloBotonPequeno}
            onPress={() => responder('si')}
          >
            <Trans>Yes, they told me</Trans>
          </Boton>
          <Boton
            variante="fantasma"
            style={estiloBotonPequeno}
            onPress={() => responder('no')}
          >
            <Trans>No, I use more</Trans>
          </Boton>
          <Boton
            variante="fantasma"
            style={estiloBotonPequeno}
            onPress={() => responder('nose')}
          >
            <Trans>I do not know</Trans>
          </Boton>
        </View>
      )}

      {preguntaCoche && (
        <View style={{ flexDirection: 'row', gap: 8 }}>
          <Boton
            variante="aviso"
            style={estiloBotonPequeno}
            onPress={() => responder('si')}
          >
            <Trans>Yes</Trans>
          </Boton>
          <Boton
            variante="fantasma"
            style={estiloBotonPequeno}
            onPress={() => responder('no')}
          >
            <Trans>No</Trans>
          </Boton>
        </View>
      )}

      {guion && txt.guion && (
        <View
          data-testid="gavi-guion"
          style={{
            gap: 6,
            padding: '10px 12px',
            borderRadius: radio.boton,
            backgroundColor: color.warnSoft,
          }}
        >
          <Text style={{ ...texto.etiqueta, color: color.warn }}>
            <Trans>What to say to them</Trans>
          </Text>
          {txt.guion.map(frase => (
            <Text key={frase} style={{ ...texto.cuerpo, color: color.fg }}>
              {`«${frase}»`}
            </Text>
          ))}
        </View>
      )}

      {!preguntaCoche && !preguntaConsumo && (
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
          {txt.guion && (
            <Boton
              variante="aviso"
              style={estiloBotonPequeno}
              onPress={() => setGuion(g => !g)}
              aria-expanded={guion}
            >
              <Trans>What do I say?</Trans>
            </Boton>
          )}
          <Boton
            variante="fantasma"
            style={estiloBotonPequeno}
            onPress={() => {
              registrarUso('gavi_pagos_fijos', {
                accion: 'recordar',
                tipo: consejo.id,
              });
              cambiar(e => conRecordar(e, consejo.clave, recordar));
            }}
          >
            {t('Remind me on {{date}}', { date: formatos.fecha(recordar) })}
          </Boton>
          <Boton
            variante="fantasma"
            style={estiloBotonPequeno}
            onPress={silenciar}
          >
            {txt.guion ? <Trans>I have called</Trans> : <Trans>Got it</Trans>}
          </Boton>
        </View>
      )}

      {txt.fuente && (
        <Text style={{ fontSize: 12, color: color.fg3 }}>{txt.fuente}</Text>
      )}
    </View>
  );
}

type CampoFecha = 'renueva' | 'permanencia' | 'finPromo';

function TarjetaContrato({
  nombreCategoria,
  serie,
  conComercio,
  contrato,
  cambiarContrato,
  inicioCuenta,
  hoy,
}: {
  nombreCategoria: string;
  serie: SerieCobro;
  /** Con varios recibos en la categoría: el comercio en la cabecera. */
  conComercio: boolean;
  contrato: Contrato | undefined;
  cambiarContrato: (cambios: Partial<Contrato>) => void;
  inicioCuenta: ReadonlyMap<string, string>;
  hoy: string;
}) {
  const { t } = useTranslation();
  const { fecha } = useFormatosGavi();
  const textoTipo = useTextoTipo();
  const [editando, setEditando] = useState<CampoFecha | 'tipo' | null>(null);

  const tipo = tipoDeSerie(serie, nombreCategoria, contrato);
  const renueva = fechaRenovacion(serie, tipo, contrato, hoy, inicioCuenta);
  const confirmado = contrato?.confirmado === true;
  const deGavi = !confirmado && (tipo != null || renueva != null);

  const guardar = (cambios: Partial<Contrato>) => {
    registrarUso('gavi_pagos_fijos', { accion: 'contrato' });
    cambiarContrato(cambios);
  };
  const confirmar = () =>
    guardar({
      confirmado: true,
      tipo: tipo ?? undefined,
      renueva: contrato?.renueva ?? renueva ?? undefined,
    });

  const filas: { campo: CampoFecha; etiqueta: string; valor?: string }[] = [
    {
      campo: 'renueva',
      etiqueta: t('Renews on'),
      valor: contrato?.renueva ?? renueva ?? undefined,
    },
    {
      campo: 'permanencia',
      etiqueta: t('Commitment until'),
      valor: contrato?.permanencia,
    },
    {
      campo: 'finPromo',
      etiqueta: t('Discount ends'),
      valor: contrato?.finPromo,
    },
  ];

  return (
    <View
      style={{ ...estiloTarjeta('normal', 0), overflow: 'hidden' }}
      data-testid="gavi-contrato"
    >
      <View style={{ padding: '12px 16px 8px', gap: 2 }}>
        <Text style={{ fontSize: 15, fontWeight: 800, color: color.fg }}>
          {conComercio ? (
            `${t('Contract')} · ${serie.comercio}`
          ) : (
            <Trans>Contract</Trans>
          )}
        </Text>
        {deGavi && (
          <Text style={{ fontSize: 12, color: color.fg3 }}>
            <Trans>Gavi took it from your history</Trans>
          </Text>
        )}
      </View>

      {tipo == null ? (
        <View
          style={{
            borderTop: `1px solid ${color.line}`,
            padding: '10px 16px',
            gap: 8,
          }}
        >
          <Text style={{ ...texto.cuerpo, color: color.fg }}>
            <Trans>What is this?</Trans>
          </Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
            {TIPOS_RECIBO.map(tp => (
              <Boton
                key={tp}
                variante="fantasma"
                style={{ minHeight: 36, padding: '0 12px', fontSize: 13 }}
                onPress={() => guardar({ tipo: tp })}
                data-testid={`gavi-tipo-${tp}`}
              >
                {`${EMOJI_TIPO[tp]} ${textoTipo(tp)}`}
              </Boton>
            ))}
          </View>
        </View>
      ) : (
        <FilaContrato
          etiqueta={t('Type')}
          valor={`${EMOJI_TIPO[tipo]} ${textoTipo(tipo)} · ${serie.comercio}`}
          onPress={() => setEditando('tipo')}
        />
      )}

      {filas.map(f => (
        <FilaContrato
          key={f.campo}
          etiqueta={f.etiqueta}
          valor={f.valor ? fecha(f.valor) : undefined}
          onPress={() => setEditando(f.campo)}
        />
      ))}

      {deGavi && (
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 8,
            borderTop: `1px solid ${color.line}`,
            padding: '8px 16px 12px',
          }}
        >
          <Text style={{ ...texto.cuerpo, flex: 1, color: color.fg2 }}>
            <Trans>Is that right?</Trans>
          </Text>
          <Boton
            variante="primario"
            style={estiloBotonPequeno}
            onPress={confirmar}
            data-testid="gavi-contrato-si"
          >
            <Trans>Yes</Trans>
          </Boton>
          <Boton
            variante="fantasma"
            style={estiloBotonPequeno}
            onPress={() => setEditando(tipo == null ? 'renueva' : 'tipo')}
          >
            <Trans>Change</Trans>
          </Boton>
        </View>
      )}

      {editando === 'tipo' && (
        <Hoja aria-label={t('Type')} onCerrar={() => setEditando(null)}>
          <Text style={{ ...texto.titulo, color: color.fg, marginBottom: 8 }}>
            <Trans>What is this?</Trans>
          </Text>
          {TIPOS_RECIBO.map(tp => (
            <Button
              key={tp}
              variant="bare"
              onPress={() => {
                guardar({ tipo: tp });
                setEditando(null);
              }}
              style={{
                minHeight: TACTIL + 4,
                justifyContent: 'flex-start',
                gap: 10,
                fontSize: 15,
                fontWeight: tp === tipo ? 800 : 600,
                color: color.fg,
                backgroundColor: 'transparent',
              }}
            >
              {`${EMOJI_TIPO[tp]} ${textoTipo(tp)}`}
            </Button>
          ))}
        </Hoja>
      )}

      {editando != null && editando !== 'tipo' && (
        <HojaFecha
          etiqueta={filas.find(f => f.campo === editando)?.etiqueta ?? ''}
          valor={filas.find(f => f.campo === editando)?.valor ?? ''}
          onGuardar={valor => {
            guardar({ [editando]: valor ?? undefined, confirmado: true });
            setEditando(null);
          }}
          onCerrar={() => setEditando(null)}
        />
      )}
    </View>
  );
}

function FilaContrato({
  etiqueta,
  valor,
  onPress,
}: {
  etiqueta: string;
  valor?: string;
  onPress: () => void;
}) {
  return (
    <Button
      variant="bare"
      onPress={onPress}
      style={{
        minHeight: TACTIL + 4,
        borderRadius: 0,
        borderTop: `1px solid ${color.line}`,
        justifyContent: 'space-between',
        padding: '6px 16px',
        gap: 12,
        backgroundColor: 'transparent',
      }}
    >
      <Text style={{ fontSize: 14, color: color.fg2 }}>{etiqueta}</Text>
      <Text
        style={{
          fontSize: 14,
          fontWeight: 700,
          color: valor ? color.fg : color.accent,
          textAlign: 'right',
        }}
      >
        {valor ?? <Trans>Add date</Trans>}
      </Text>
    </Button>
  );
}

function HojaFecha({
  etiqueta,
  valor,
  onGuardar,
  onCerrar,
}: {
  etiqueta: string;
  valor: string;
  onGuardar: (valor: string | null) => void;
  onCerrar: () => void;
}) {
  const [fecha, setFecha] = useState(valor);
  return (
    <Hoja aria-label={etiqueta} onCerrar={onCerrar}>
      <View style={{ gap: 12 }}>
        <Text style={{ ...texto.titulo, color: color.fg }}>{etiqueta}</Text>
        <InputField
          aria-label={etiqueta}
          type="date"
          value={fecha}
          onChangeValue={setFecha}
          data-testid="gavi-contrato-fecha"
          style={{
            minHeight: TACTIL + 4,
            width: '100%',
            marginLeft: 0,
            marginRight: 0,
            boxSizing: 'border-box',
            fontSize: 16,
            borderRadius: radio.boton,
          }}
        />
        <Boton
          bloque
          onPress={() =>
            onGuardar(/^\d{4}-\d{2}-\d{2}$/.test(fecha) ? fecha : null)
          }
        >
          <Trans>Save</Trans>
        </Boton>
        {valor && (
          <Boton bloque variante="fantasma" onPress={() => onGuardar(null)}>
            <Trans>Remove date</Trans>
          </Boton>
        )}
      </View>
    </Hoja>
  );
}

function DadoDeBaja({
  serie,
  conComercio,
  fecha,
  cambiar,
  hoy,
}: {
  serie: SerieCobro;
  conComercio: boolean;
  fecha: string | null;
  cambiar: (fn: (e: EstadoGavi) => EstadoGavi) => void;
  hoy: string;
}) {
  const { t } = useTranslation();
  const formatos = useFormatosGavi();
  if (fecha) {
    return (
      <View
        style={{
          ...estiloTarjeta('punteada', 14),
          flexDirection: 'row',
          alignItems: 'center',
          gap: 10,
        }}
        data-testid="gavi-baja"
      >
        <Text style={{ ...texto.cuerpo, flex: 1, color: color.fg2 }}>
          {t(
            'Cancelled on {{date}}. If {{name}} charges you again, Gavi will tell you.',
            { date: formatos.fecha(fecha), name: serie.comercio },
          )}
        </Text>
        <Boton
          variante="fantasma"
          style={estiloBotonPequeno}
          onPress={() => cambiar(e => conBaja(e, serie.clave, null))}
        >
          <Trans>Undo</Trans>
        </Boton>
      </View>
    );
  }
  return (
    <Boton
      variante="fantasma"
      onPress={() => {
        registrarUso('gavi_pagos_fijos', { accion: 'baja' });
        cambiar(e => conBaja(e, serie.clave, hoy));
      }}
      data-testid="gavi-dar-de-baja"
    >
      {conComercio ? (
        t('I have cancelled {{name}}', { name: serie.comercio })
      ) : (
        <Trans>I have cancelled it</Trans>
      )}
    </Boton>
  );
}
